import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { Font } from '../../../constants'
import { Callback } from '../../../modules/callback'

import { Locale } from '../../../modules/locale'
import {
  applyMask,
  escapeHtml,
  generateId,
  MaskOptions,
  parseInlineStyle,
} from '../../../modules/utils'

interface InputFieldChangeEvent {
  fieldId: string
  value: string
  onChange: string
}

export interface LvcInput {
  value: string
  internalValue: string
  defaultValue: string
  tooltipOpened: boolean
  wordLimit: number
  charactersLimit: null | number
  dataAlternativeClearIcon: boolean
  padding: string
  debounceDelay: number
}

Locale.getInstance().registerKeySet('fr', 'lvcInput', {
  wordLimitNote: 'mots maximum',
})

Locale.getInstance().registerKeySet('en', 'lvcInput', {
  wordLimitNote: 'words max',
})

export class LvcInput extends MinzeElement {
  inputId: string = generateId()
  isFocused: boolean = false
  selectionStart: number = 0
  selectionEnd: number = 0
  reactive: Reactive = [
    ['internal-value', '', false],
    ['tooltipOpened', false],
    ['charactersLimit', null],
    ['value', '', true],
  ]

  attrs: Attrs = [
    'autocomplete',
    ['disabled', false],
    ['placeholder', ''],
    'height',
    'type',
    'mask',
    ['error', false],
    'name',
    'id',
    ['default-value', ''],
    ['date-format', 'YYYY-MM-DD'],
    'label',
    'padding',
    'fontSize',
    'font-weight',
    ['marginTop', '4px'],
    ['background', 'white'],
    ['clear-button-enabled', false],
    ['data-alternative-clear-icon', false],
    'placeholder-color',
    'border',
    ['inner-style', ''],
    ['readonly', false],
    ['tooltip', false],
    ['textarea', false],
    ['on-change', null],
    ['secondary-style', false],
    ['focus-on-rerender', false],
    ['auto-focus', false],
    ['resize', 'both'],
    ['wordLimit'],
    ['debounce-delay', 150], // Default debounce delay in milliseconds
  ]

  static observedAttributes = [
    'inner-style',
    'placeholder',
    'height',
    'type',
    'name',
    'default-value',
    'font-size',
    'font-weight',
    'padding',
    'error',
    'label',
  ]

  fieldType: string = 'text'

  // Debounce timer for input changes
  private _debounceTimeout: number | null = null

  onDestroy() {
    // Clean up any pending debounce timer
    if (this._debounceTimeout) {
      window.clearTimeout(this._debounceTimeout)
      this._debounceTimeout = null
    }
  }

  onRender() {
    if (this.shadowRoot) {
      const input = this.shadowRoot.querySelector('input') as HTMLInputElement
      if (input && this.focusOnRerender) {
        input.focus()
        input.selectionStart = this.selectionStart
        input.selectionEnd = this.selectionEnd
      }
    }
  }

  beforeRender() {
    if (this.shadowRoot) {
      if (
        this.shadowRoot.activeElement &&
        this.shadowRoot.activeElement === this.shadowRoot.querySelector('input')
      ) {
        this.selectionStart =
          (this.shadowRoot.activeElement as HTMLInputElement)?.selectionStart || 0
        this.selectionEnd = (this.shadowRoot.activeElement as HTMLInputElement)?.selectionEnd || 0
        this.isFocused = true
      } else {
        this.isFocused = false
      }
    }
  }

  onAttributeChange = (name: string, oldValue: string, newValue: string) => {
    if (name === 'type') {
      this.fieldType = newValue
    }

    if (name === 'value' && oldValue !== newValue) {
      this.internalValue = newValue
      this.value = escapeHtml(newValue)
    }

    if (name === 'default-value' && oldValue !== newValue) {
      this.internalValue = newValue
      this.value = escapeHtml(newValue)
      this.rerender()
    }
    if (name === 'label' && oldValue !== newValue) {
      this.label = newValue
    }
  }
  handleInput = (event: Event) => {
    const currentValue = this.internalValue
    const target = event.target as HTMLInputElement
    let value = target.value
    let pressedKey = (event as KeyboardEvent)?.key as string

    if (this.mask) {
      // Only apply mask if user is typing (not deleting)
      // This prevents separators from being re-added when user backspaces/deletes
      const isDeleting = value.length < currentValue.length

      if (!isDeleting) {
        value = applyMask(value, {
          mask: this.mask as string,
          patternsMap: {
            A: /[a-zA-Z]/,
            '#': /\d/,
            '*': /./,
          } as MaskOptions['patternsMap'],
        })
        target.value = value
      }
    }

    // HACK: Handle textarea 'Enter' key for new lines, because of Minze.
    if (this.textarea && pressedKey === 'Enter') {
      value += '\n'
      target.value = value
    }
    // Update internal value immediately
    this.internalValue = value
    this.value = escapeHtml(value)

    // Handle word limit logic
    let wordsCount = value.trim().split(/\s+/).length || 0
    if (value[value.length - 1] === ' ') wordsCount++
    const wordLimitRespected = this.wordLimit ? wordsCount <= this.wordLimit : true
    if (value.length < this.value.length || wordLimitRespected) {
      this.charactersLimit = null
    } else {
      this.charactersLimit = this.value.length
    }

    // Trigger UI update if needed
    if (!currentValue || !value) this.rerender()

    // Clear any existing debounce timer
    if (this._debounceTimeout) {
      window.clearTimeout(this._debounceTimeout)
    }

    // Get debounce delay (default to 150ms if not specified)
    const delay =
      typeof this.debounceDelay === 'number'
        ? this.debounceDelay
        : parseInt(this.debounceDelay as string) || 150

    // Set up debounced callback
    if (delay > 0) {
      this._debounceTimeout = window.setTimeout(() => {
        // Send the field name and value to make parent component handling easier
        Callback.call(this.onChange as string, this.internalValue)
        this._debounceTimeout = null
      }, delay)
    } else {
      // If delay is 0, call immediately
      Callback.call(this.onChange as string, this.internalValue)
    }
  }

  clearInput = () => {
    if (this.internalValue === '') return

    // Clear any existing debounce timer
    if (this._debounceTimeout) {
      window.clearTimeout(this._debounceTimeout)
      this._debounceTimeout = null
    }

    this.internalValue = ''
    this.value = ''
    this.rerender(true)

    // Send the cleared value immediately
    Callback.call(this.onChange as string, '')
  }

  onPasswordVisibilityChange = () => {
    this.showPassword = !this.showPassword
    this.rerender()
  }

  closeTooltip = () => (this.tooltipOpened = false)
  openTooltip = () => (this.tooltipOpened = true)

  html = () => {
    let fieldType = this.fieldType
    let fieldValue = escapeHtml(this.internalValue)

    if (fieldType === 'password' && this.showPassword) {
      fieldType = 'text'
    }

    let showError = typeof this.error === 'string' && this.error.length > 0

    return `
      <lvc-box height="auto">
        ${
          this.label
            ? `
          <lvc-box display="flex" alignItems="center">
            <label for="${this.inputId}">${this.label}</label>
            ${this.tooltipOpened ? `<div class="close-overlay"></div>` : ''}
            ${
              this.tooltip
                ? `
              <lvc-box ml="4px" position="relative">
                <lvc-help-icon></lvc-help-icon>
                <dialog class="tooltip" ${this.tooltipOpened ? 'open' : ''}>
                  <slot name="tooltip-content"></slot>
                </dialog>
              </lvc-box>
            `
                : ``
            }
          </lvc-box>
        `
            : ''
        }
        <div class="input-container">
        ${
          this.textarea
            ? `
          <textarea
            ${this.disabled ? `disabled` : ''}
            placeholder="${this.placeholder || ''}"
            ${fieldType ? `type="${fieldType}"` : ''}
            data-main-input="true"
            name="${this.name}"
            id="${this.inputId}"
            ${fieldValue ? `value="${fieldValue}"` : ''}
            mask="${this.mask}"
            class="input ${this.error ? 'error' : ''} ${fieldValue ? 'has-value' : ''}"
            ${this.readonly ? `readonly` : ''}
            ${this.charactersLimit ? `maxlength="${this.charactersLimit}"` : ''}
          >${fieldValue}</textarea>
        `
            : `
          <input
            ${this.autocomplete ? `autocomplete="${this.autocomplete}"` : ''}
            ${this.disabled ? `disabled` : ''}
            placeholder="${this.placeholder || ''}"
            ${fieldType ? `type="${fieldType}"` : ''}
            data-main-input="true"
            name="${this.name}"
            id="${this.inputId}"
            ${fieldValue ? `value="${fieldValue}"` : ''}
            mask="${this.mask}"
            class="input ${this.error ? 'error' : ''} ${fieldValue ? 'has-value' : ''}"
            ${this.readonly ? `readonly` : ''}
            ${this.autoFocus ? `autofocus` : ``}
            ${this.charactersLimit ? `maxlength="${this.charactersLimit}"` : ''}
          />
        `
        }
        ${
          this.clearButtonEnabled
            ? `
          ${
            fieldValue
              ? `
            <button name="clearInput" class="clear-input-button">
              ${
                this.dataAlternativeClearIcon
                  ? `<lvc-search-cross-icon></lvc-search-cross-icon>`
                  : `<lvc-clear-input-icon></lvc-clear-input-icon>`
              }
            </button>
          `
              : ''
          }
        `
            : ``
        }
        ${
          this.type == 'password'
            ? `
          <button name="showPassword" class="show-password-button">
            ${this.showPassword ? `<lvc-show-password-icon></lvc-show-password-icon>` : `<lvc-hide-password-icon></lvc-hide-password-icon>`}
          </button>
        `
            : ``
        }
        </div>
        ${
          this.wordLimit
            ? `
          <lvc-text font-preset="small" font-size="12px">
            ${this.wordLimit} ${Locale.getKey('lvcInput.wordLimitNote')}
          </lvc-text>
        `
            : ''
        }${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
      </lvc-box>
      
      `
  }

  css = () => {
    return `
    :host {
      font-family: ${Font.family};
      width: 100%;
      position: relative;
      box-sizing: border-box;
      display: block;
    }
   
    .input:focus-visible {
      outline: unset;
    }
    .input-container {
      position: relative;
      ${this.marginTop ? `margin-top: ${this.marginTop};` : `margin-top: 4px;`}
    }

    .input {
      font-family: ${Font.family};
      width: 100%;
      border: ${this.border ? this.border : 'unset'};
      height: ${this.height ? this.height : '48px'};
      font-size: ${this.fontSizeValue ? this.fontSizeValue : '16px'};
      padding: ${this.padding ? this.padding : '12px 16px'};
      font-weight: ${this.fontWeight ? this.fontWeight : Font.weight.regular};
      background: ${this.background ? this.background : '#fff'};
      color: #1E007D;
      border-radius: 60px;
      ${this.readonly ? `cursor: default;` : ''}
    }
    
    textarea.input {
      height: 200px;
      border: 1px solid rgba(30, 0, 125, 0.30);
      resize: ${this.resize};
      border-radius: 20px;
    }
    
    ${
      this.placeholderColor
        ? `
      ::placeholder {
        color: ${this.placeholderColor};
        opacity: 1;
      }
      
      :-ms-input-placeholder {
        color: ${this.placeholderColor};
      }
      
      ::-ms-input-placeholder {
        color: ${this.placeholderColor};
      }
    `
        : ``
    }

    label {
      font-family: ${Font.family};
      color:  #1E007D;
      font-size: 14px;
      line-height: 17px;
      font-weight: ${Font.weight.bold};
      margin-bottom: 4px;
    }

    .show-password-button {
      cursor: pointer;
      position: absolute;
      right: 5px;
      top: 50%;
      transform: translateY(-50%);
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
    }
    
    .show-password-button lvc-close-icon {
    }
    
    .clear-input-button {
      cursor: pointer;
      position: absolute;
      right: 5px;
      width: 28px;
      height: 28px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      justify-content: center;
      align-items: center;
      background: transparent;
      border: none;
    }
    
    .clear-input-button lvc-close-icon {
    }
   
    .error {
      background: #FFEBD7;
      border-bottom: 3px solid #FF4600;
    }
    
    lvc-show-password-icon, lvc-hide-password-icon {
      width: 20px;
      height: 20px;
    }
    
    lvc-help-icon {
      cursor: pointer;
    }
    
    .tooltip {
      z-index: 1;
      border: 1px solid rgba(30, 0, 125, 0.3);
      box-shadow: 0px 4px 16px rgba(15, 0, 62, 0.15);
      border-radius: 8px;
      padding: 16px;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 150%;
      color: #1E007D;
      max-width: 90vw;
      width: 340px;
    }
    
    .close-overlay {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1;
    }
    
    ${
      this.secondaryStyle
        ? `
      .input, textarea.input {
        background: transparent;
        border: 1px solid #1E007D;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 150%;
      }
      .input:focus, .input.has-value {
        border: 1px solid rgba(30, 0, 125, 0.3);
        background: #BFF0FF;
      }
      ::placeholder {
        color: rgba(30, 0, 125, 0.5);
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 150%;
      }
      :-ms-input-placeholder {
        color: rgba(30, 0, 125, 0.5);
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 150%;
      }
      ::-ms-input-placeholder {
        color: rgba(30, 0, 125, 0.5);
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 150%;
      }
      label {
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 150%;
        margin-bottom: 4px;
      }
      .input-container {
        margin-top: 0;
      }
    `
        : ''
    }
 
    ${parseInlineStyle(this.innerStyle as string)}
  `
  }

  eventListeners: EventListeners = [
    ['input', 'keyup', this.handleInput.bind(this)],
    ['input', 'change', this.handleInput.bind(this)],
    ['textarea', 'keyup', this.handleInput.bind(this)],
    ['textarea', 'change', this.handleInput.bind(this)],
    ['.clear-input-button', 'click', this.clearInput.bind(this)],
    ['[name="showPassword"]', 'click', this.onPasswordVisibilityChange.bind(this)],
    ['lvc-help-icon', 'click', this.openTooltip],
    ['.close-overlay', 'click', this.closeTooltip],
  ]
}
